/**
 * RequireCapability — route guard, capability variant.
 *
 * Drop-in replacement for RequireRole during the Wave 2/3 migration.
 * Reads claims?.role from AuthContext, checks against the shared
 * ROLE_CAPABILITIES matrix.
 *
 * NO automatic founder bypass. Per CLAUDE.md §7 + 2026-04-29 review,
 * founder is admin-portal-only. Founders that somehow land on a client
 * app route fall through to the standard deny path → Navigate to '/'.
 * Defence in depth: the AuthContext root layer ALSO redirects founders
 * out of the client app (apps/admin login is the entry point, not
 * apps/client).
 *
 * Both single-cap and OR-semantic (array) forms supported. The
 * `requireAny` form is for sites where the original
 * `enforceRole([admin, broker])` allows multiple roles per their
 * capability — the inline self-scope check at the consumer's
 * implementation stays exactly as-is.
 *
 * Per-tenant kill-switch (OPEN-2026-05-05-D resolved 2026-05-05):
 *   When `companies/{companyId}.featureFlags.capabilityRbacEnabled === false`,
 *   the guard falls back to a role-allow-list passed inline as `legacyAllow`.
 *   Each migrated guard carries the pre-migration role allow-set on its
 *   props; PR G cleanup deletes the prop along with this fallback branch
 *   in one mechanical pass. No frozen-snapshot table — single source of
 *   truth for matrix rules stays in capabilities.ts.
 *
 *   Default behaviour (no flag, or flag !== false): cap-driven path.
 *   Default fallback (flag === false but no `legacyAllow`): fail-closed.
 */

import React from 'react';
import { useOutletContext, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCompany } from '../../context/CompanyContext';
// Granular subpath imports — the `/auth` barrel re-exports `computeBrokerIds`
// which pulls in Node's `crypto` module, breaking webpack 5 client bundles.
// See packages/shared-utils/package.json exports + commit d95778a8 history.
import {
  hasCapability,
  hasAnyCapability,
  type Capability,
} from '@valutten/shared-utils/capabilities';
import type { AppRole } from '@valutten/shared-utils/roles';

interface RequireCapabilityProps {
  /** Single required capability. Use `requireAny` for OR-semantics. */
  capability?: Capability;
  /** OR-semantic: pass if role has ANY of these caps. Inline self-scope checks at the consumer level still apply. */
  requireAny?: readonly Capability[];
  /**
   * Pre-migration role allow-list. Used ONLY when the per-tenant kill-switch
   * (`companies/{id}.featureFlags.capabilityRbacEnabled === false`) is active.
   * Deleted along with this fallback branch in PR G cleanup.
   */
  legacyAllow?: readonly AppRole[];
}

interface AppOutletContext {
  userData?: {
    role?: string;
  };
}

const RequireCapability: React.FC<RequireCapabilityProps> = ({
  capability,
  requireAny,
  legacyAllow,
}) => {
  const outletContext = useOutletContext<AppOutletContext>();
  const { claims } = useAuth();
  const { company } = useCompany();
  // Prefer Firestore-doc role (outletContext) for parity with RequireRole's
  // current behaviour. Fall back to claims if outletContext is unavailable.
  const role = (outletContext?.userData?.role ?? claims?.role ?? null) as AppRole | null;

  if (!role) {
    return <Navigate to="/" replace />;
  }

  // Kill-switch: per-tenant rollback parity with backend
  // enforceCapabilityShadow. Default-true (no field, undefined company
  // doc, or flag === true) keeps the cap-driven path. Strict equality
  // to `false` is the only short-circuit — first-paint loading state
  // (company === null) stays on the cap path so a stale legacy lookup
  // doesn't accidentally re-grant during initial render.
  const rbacDisabled = company?.featureFlags?.capabilityRbacEnabled === false;
  if (rbacDisabled && legacyAllow) {
    return legacyAllow.includes(role)
      ? <Outlet context={outletContext} />
      : <Navigate to="/" replace />;
  }

  let allowed = false;
  if (capability) {
    allowed = hasCapability(role, capability);
  } else if (requireAny && requireAny.length > 0) {
    allowed = hasAnyCapability(role, requireAny);
  } else {
    // Misconfigured guard — fail-closed.
    return <Navigate to="/" replace />;
  }

  return allowed ? <Outlet context={outletContext} /> : <Navigate to="/" replace />;
};

export default RequireCapability;
